import { useConfigurations } from "@via/api/configuration.tsx";
import {
  useCheckAuthStatus,
  useCheckAuthenticated,
} from "@via/api/auth.api.tsx";
import { ConfigurationsResponse, UserInfoResponse } from "common";
import {
  createContext,
  type JSX,
  type ReactNode,
  useEffect,
  useState,
} from "react";

interface Props {
  children?: ReactNode;
}

interface IAuthContext {
  user: UserInfoResponse | boolean | null;
  setUser: (newState: UserInfoResponse | boolean | null) => void;
  config: ConfigurationsResponse | null;
  setConfig: (newState: null) => void;
}

const initial = {
  user: false,
  setUser: (): void => {},
  config: null,
  setConfig: (): void => {},
};

// Create AuthContext
const AuthContext = createContext<IAuthContext>(initial);

// Create the auth context provider
const AuthProvider = ({ children }: Props): JSX.Element => {
  const [user, setUser] = useState<UserInfoResponse | boolean | null>(null);
  const { data: authStatus, isLoading: isAuthStatusLoading } =
    useCheckAuthStatus();

  const { data: userData } = useCheckAuthenticated(authStatus ?? null);
  const { data: configurations } = useConfigurations();
  const [config, setConfig] = useState<ConfigurationsResponse | null>(null);

  useEffect(() => {
    // First check authentication status
    if (!isAuthStatusLoading) {
      if (authStatus === false) {
        setUser(false);
      } else if (authStatus === true && userData) {
        setUser(userData);
      }
    }

    if (configurations) {
      setConfig(configurations);
    }
  }, [authStatus, userData, configurations, isAuthStatusLoading]);

  return (
    <AuthContext.Provider value={{ user, setUser, config, setConfig }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
